import { Popover } from "@headlessui/react";
import { ChevronRightIcon } from "@heroicons/react/outline";
import { useEffect, useState } from "react";
import ReactDatePicker from "react-datepicker";
import "./index.scss";
import moment from "moment";
import { DayCalendar } from "./dayCalendar";
import { WeekCalendar } from "./weekCalendar";
import { MonthCalendar } from "./monthCalendar";
import { QuarterCalendar } from "./quarterCalendar";
import { YearCalendar } from "./yearCalendar";
import { DatePickerFilterOption } from "interfaces";

interface IOption {
  id: DatePickerFilterOption;
  label: string;
  startDate?: Date;
  endDate?: Date;
}

const READONLY_OPTIONS: IOption[] = [
  {id: DatePickerFilterOption.TODAY, label: "Today", startDate: moment().toDate(), endDate: moment().toDate()},
  {id: DatePickerFilterOption.YESTERDAY, label: "Yesterday", startDate: moment().subtract(1, 'day').toDate(), endDate: moment().subtract(1, 'day').toDate()},
  {id: DatePickerFilterOption.SEVENDAYS, label: "Last 7 days", startDate: moment().subtract(7, 'day').toDate(), endDate: moment().subtract(1, 'day').toDate()},
  {id: DatePickerFilterOption.THIRTYDAYS, label: "Last 30 days", startDate: moment().subtract(30, 'day').toDate(), endDate: moment().subtract(1, 'day').toDate()},
];

const DATEPICKER_OPTIONS: IOption[] = [
  {id: DatePickerFilterOption.DAYS, label: "Day(s)"},
  {id: DatePickerFilterOption.WEEK, label: "Week"},
  {id: DatePickerFilterOption.MONTH, label: "Month"},
  {id: DatePickerFilterOption.QUARTER, label: "Quarter"},
  {id: DatePickerFilterOption.YEAR, label: "Year"}
]

interface DatePickerInputProps {
  defaultOption?: DatePickerFilterOption;
  setDateOption: (option: any) => void;
  defaultStartDate?: Date | null;
  defaultEndDate?: Date | null;
  _handleDateChange: (data: any) => void;
}
export const DatePickerInput = ({defaultOption = DatePickerFilterOption.DAYS, setDateOption, defaultStartDate, defaultEndDate, _handleDateChange} :DatePickerInputProps) => {

  const [startDate, setStartDate] = useState<Date | null>(defaultStartDate ?? null);
  const [endDate, setEndDate] = useState<Date | null>(defaultEndDate ?? null);

  const [triggerResetState, setTriggerResetState] = useState(false);

  const [selectedFilerOptionId, setSelectedFilterOptionId] = useState<DatePickerFilterOption>(defaultOption);

  const onChange = (dates: any) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const _handleClickReadOnlyOption = (option: IOption) => {
    if (selectedFilerOptionId !== option.id) {
      setSelectedFilterOptionId(option.id);
      onChange([option.startDate, option.endDate]);
    }
  }

  const _handleClickDatePickerOption = (option: IOption) => {
    setSelectedFilterOptionId(option.id);
    onChange([null, null])
  }

  const _renderReadOnlyDatePicker = () => {
    return (
      <ReactDatePicker
        onChange={() => {}}
        calendarStartDay={1}
        startDate={startDate}
        endDate={endDate}
        calendarClassName="!border-0"
        wrapperClassName="!flex-1"
        selectsRange
        inline
      />
    )
  };

  const _renderFilterDatePicker = () => {
    const calendarProps = {
      startDate,
      endDate,
      onChange: (dates: any) => {
        onChange(dates);
      }
    }
    switch (selectedFilerOptionId) {
      case DatePickerFilterOption.DAYS: return <DayCalendar {...calendarProps} />;
      case DatePickerFilterOption.WEEK: return <WeekCalendar {...calendarProps} />;
      case DatePickerFilterOption.MONTH: return <MonthCalendar {...calendarProps} />;
      case DatePickerFilterOption.QUARTER: return <QuarterCalendar {...calendarProps} />;
      case DatePickerFilterOption.YEAR: return <YearCalendar {...calendarProps} />;
      default: return;
    }
  }

  const isReadOnly = READONLY_OPTIONS.find(option => option.id === selectedFilerOptionId);

  useEffect(() => {
    if (triggerResetState) {
      setStartDate(defaultStartDate ?? null);
      setEndDate(defaultEndDate ?? null);
      setSelectedFilterOptionId(defaultOption);
      setTriggerResetState(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerResetState])

  return (
    <Popover className="relative w-full">
      <Popover.Button 
        className="w-full text-left border border-solid border-gray-300 focus:!outline-none rounded-lg px-2.5 py-2"
        onClick={() => setTriggerResetState(true)}
      >
        <span className="text-sm">
          {defaultStartDate ? moment(defaultStartDate).format("DD/MM/YYYY") : "Start Date"} - {defaultEndDate ? moment(defaultEndDate).format("DD/MM/YYYY") : "End Date"}
        </span>
      </Popover.Button>

      <Popover.Panel className="absolute z-10 pt-2 w-full sm:w-[400px]">
        {({ close }) => (
          <div className="flex bg-white rounded-md shadow-md border border-solid border-gray-100">
            <div className="px-2 w-28 shrink-0 box-border sm:w-32 sm:px-4">
              <div className="flex flex-col gap-y-2 py-3 text-sm">
                {READONLY_OPTIONS.map(option => (
                  <button 
                    className={`text-left ${selectedFilerOptionId === option.id ? "text-brand-600 font-semibold" : ""}`}
                    key={option.id}
                    onClick={() => _handleClickReadOnlyOption(option)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-col gap-y-2 text-sm py-3 border-t border-gray-200">
                {DATEPICKER_OPTIONS.map(option => (
                  <button 
                    key={option.id}
                    className={`flex justify-between items-center ${selectedFilerOptionId === option.id ? "text-brand-600 font-semibold" : ""}`}
                    onClick={() => _handleClickDatePickerOption(option)}
                  >
                    {option.label}
                    <ChevronRightIcon className="w-3 h-3" />
                  </button>
                ))}
              </div>
            </div>
            <div className="mx-auto py-2 px-2">
              {isReadOnly ? _renderReadOnlyDatePicker() : _renderFilterDatePicker()}
              <div className="text-sm w-full flex justify-center items-center border-t border-gray-200 pt-3.5 pb-3.5">
                <button 
                  className="bg-brand-500 text-white px-4 py-1.5 rounded-sm w-full active:opacity-85" 
                  onClick={() => {
                    if(startDate) {
                      _handleDateChange([startDate, endDate ?? startDate]);
                      setDateOption(selectedFilerOptionId);  
                    }
                    close();
                  }}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        )}
      </Popover.Panel>
    </Popover>
  );
}